import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaCheck, FaTimes, FaUserCheck, FaExclamationCircle, FaUserClock, FaTrash } from 'react-icons/fa';
import './FamilyPrivacySettings.css';
import API_BASE_URL from '../../config/api';

const FamilyPrivacySettings = () => {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const currentUserId = parseInt(localStorage.getItem('userId') || '0');

  const formatEmail = (email) => {
    if (!email) return '';
    return email.replace(/\+[^@]+@/, '@');
  };

  const fetchConsents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/family-consent/my-consents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConsents(data);
      } else {
        setError('Não foi possível carregar as permissões familiares.');
      }
    } catch (err) {
      console.error('Error loading consents:', err);
      setError('Erro de ligação ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsents();
  }, []);

  const handleRespond = async (consentId, approve) => {
    try {
      setActionLoading(consentId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/family-consent/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ consentId, approve })
      });

      if (res.ok) {
        fetchConsents();
      } else {
        alert('Erro ao responder ao pedido.');
      }
    } catch (err) {
      console.error('Error responding to consent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (consentId) => {
    if (!window.confirm('Tem a certeza que pretende revogar o acesso a esta conta?')) return;

    try {
      setActionLoading(consentId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/family-consent/revoke/${consentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        fetchConsents();
      } else {
        alert('Erro ao revogar autorização.');
      }
    } catch (err) {
      console.error('Error revoking consent:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div className="family-privacy-loading">A carregar definições de privacidade...</div>;
  }

  // Incoming requests directed to me (I am grantor)
  const incomingRequests = consents.filter(c => c.grantorUserId === currentUserId && c.status === 0); // Pending
  // Active permissions I granted to others (I am grantor)
  const myGrantedConsents = consents.filter(c => c.grantorUserId === currentUserId && c.status === 1); // Approved
  // Requests I sent to others (I am grantee)
  const myOutgoingRequests = consents.filter(c => c.granteeUserId === currentUserId);

  return (
    <div className="family-privacy-settings">
      <div className="privacy-header">
        <FaShieldAlt className="header-icon" />
        <div>
          <h2>Privacidade & Autorizações Familiares (RGPD)</h2>
          <p>Gerir autorizações de acesso à sua conta e pedidos enviados a familiares maiores de idade (18+ anos).</p>
        </div>
      </div>

      {error && <div className="privacy-error">{error}</div>}

      {/* Pedidos Recebidos Pendentes */}
      {incomingRequests.length > 0 && (
        <div className="privacy-section highlight">
          <h3>
            <FaExclamationCircle className="sec-icon warning" /> Pedidos de Acesso Pendentes
          </h3>
          <p className="sec-desc">Os seguintes familiares solicitaram autorização para aceder aos seus dados e alternar para a sua conta:</p>

          <div className="consents-list">
            {incomingRequests.map(req => (
              <div key={req.id} className="consent-card pending">
                <div className="consent-user-info">
                  <strong>{req.granteeName}</strong>
                  <span>{formatEmail(req.granteeEmail)}</span>
                  {req.note && <p className="consent-note">"{req.note}"</p>}
                </div>
                <div className="consent-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleRespond(req.id, true)}
                    disabled={actionLoading === req.id}
                  >
                    <FaCheck /> Autorizar
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleRespond(req.id, false)}
                    disabled={actionLoading === req.id}
                  >
                    <FaTimes /> Recusar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Autorizações que concedi a familiares */}
      <div className="privacy-section">
        <h3>
          <FaUserCheck className="sec-icon" /> Pessoas que podem aceder à minha conta
        </h3>
        <p className="sec-desc">Familiares que possuem atualmente a sua autorização explícita para aceder aos seus dados no Portal:</p>

        {myGrantedConsents.length === 0 ? (
          <p className="empty-state">Não concedeu autorização a nenhum familiar de momento.</p>
        ) : (
          <div className="consents-list">
            {myGrantedConsents.map(c => (
              <div key={c.id} className="consent-card active">
                <div className="consent-user-info">
                  <strong>{c.granteeName}</strong>
                  <span>{formatEmail(c.granteeEmail)}</span>
                  <small>Autorizado em: {new Date(c.respondedAt || c.requestedAt).toLocaleDateString('pt-PT')}</small>
                </div>
                <button
                  className="btn-revoke"
                  onClick={() => handleRevoke(c.id)}
                  disabled={actionLoading === c.id}
                >
                  <FaTrash /> Revogar Acesso
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Meus Pedidos Enviados */}
      <div className="privacy-section">
        <h3>
          <FaUserClock className="sec-icon" /> Contas de familiares a que solicitei acesso
        </h3>
        <p className="sec-desc">Estado dos seus pedidos de autorização enviados a familiares maiores de idade:</p>

        {myOutgoingRequests.length === 0 ? (
          <p className="empty-state">Ainda não enviou pedidos de autorização a familiares.</p>
        ) : (
          <div className="consents-list">
            {myOutgoingRequests.map(c => (
              <div key={c.id} className="consent-card">
                <div className="consent-user-info">
                  <strong>{c.grantorName}</strong>
                  <span>{formatEmail(c.grantorEmail)}</span>
                </div>
                <div className="consent-status-badge">
                  {c.status === 0 && <span className="status-badge pending">Pendente</span>}
                  {c.status === 1 && <span className="status-badge approved">Autorizado</span>}
                  {c.status === 2 && <span className="status-badge rejected">Recusado</span>}
                  {c.status === 3 && <span className="status-badge revoked">Revogado</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyPrivacySettings;
